import React, { useState, useMemo } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";

const RevenueExpenseChart = ({ data = [] }) => {
    const [hoveredIdx, setHoveredIdx] = useState(null);

    // Gera o label dos 12 últimos meses (jan, fev, mar...) e força preenchimento se API vier vazia
    // Mapeia `data` injetando 0 se faltar algum dos 12 meses obrigatórios
    const chartData = useMemo(() => {
        const getLast12Months = () => {
            const months = [];
            const now = new Date();
            for (let i = 11; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const monthLabel = d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase();
                const yearLabel = d.getFullYear().toString().slice(-2);
                months.push({ matchKey: monthLabel, displayStr: `${monthLabel}/${yearLabel}` });
            }
            return months;
        };

        const last12Months = getLast12Months();
        
        return last12Months.map(item => {
            const found = data.find(d => d.month?.toUpperCase() === item.matchKey);
            return found 
                ? { ...found, month: item.matchKey, display: item.displayStr } 
                : { month: item.matchKey, display: item.displayStr, income: 0, expense: 0, net: 0 };
        });
    }, [data]);

    // Somatórios reais baseados no array padronizado
    const totalIncome = chartData.reduce((acc, curr) => acc + (curr.income || 0), 0);
    const totalExpense = chartData.reduce((acc, curr) => acc + (curr.expense || 0), 0);
    const totalNet = totalIncome - totalExpense;

    let maxVal = Math.max(...chartData.map(d => Math.max(d.income || 0, d.expense || 0))) * 1.15;
    if (maxVal === 0) maxVal = 10000;

    const formatSmallCurrency = (value) => {
        if (value >= 1000) return `R$ ${(value / 1000).toFixed(1).replace('.0', '')}k`;
        return `R$ ${value}`;
    };

    const formatFullCurrency = (value) => {
        return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    // Cores premium e suas variações
    const COLORS = {
        income: { base: "#93c5fd", hover: "#60a5fa" }, // Pastel Blue
        expense: { base: "#fca5a5", hover: "#f87171" }, // Pastel Red
        net: { base: "#86efac", hover: "#4ade80", line: "#34d399" }, // Pastel Green
        bgHover: "rgba(243, 244, 246, 0.6)"
    };

    // Gera pontos para a curva (85% da altura máxima para dar um respiro no topo)
    const incomePoints = chartData.map((item, idx) => ({
        x: (idx / Math.max(chartData.length - 1, 1)) * 100,
        y: 100 - (item.income / maxVal) * 85
    }));

    const expensePoints = chartData.map((item, idx) => ({
        x: (idx / Math.max(chartData.length - 1, 1)) * 100,
        y: 100 - (item.expense / maxVal) * 85
    }));

    const netPoints = chartData.map((item, idx) => ({
        x: (idx / Math.max(chartData.length - 1, 1)) * 100,
        y: Math.min(100 - (Math.max(item.net, 0) / maxVal) * 85, 100)
    }));

    // Gera path smooth com Catmull-Rom splines
    const buildSmoothPath = (pts) => {
        if (pts.length < 2) return "";
        const tension = 0.3;

        let path = `M ${pts[0].x} ${pts[0].y}`;

        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[Math.max(0, i - 1)];
            const p1 = pts[i];
            const p2 = pts[i + 1];
            const p3 = pts[Math.min(pts.length - 1, i + 2)];

            const cp1x = p1.x + (p2.x - p0.x) * tension;
            const cp1y = p1.y + (p2.y - p0.y) * tension;
            const cp2x = p2.x - (p3.x - p1.x) * tension;
            const cp2y = p2.y - (p3.y - p1.y) * tension;

            path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
        }

        return path;
    };

    const incomePath = buildSmoothPath(incomePoints);
    const incomeArea = incomePath ? `${incomePath} L 100 100 L 0 100 Z` : "";

    const expensePath = buildSmoothPath(expensePoints);
    const expenseArea = expensePath ? `${expensePath} L 100 100 L 0 100 Z` : "";
    
    const netPath = buildSmoothPath(netPoints);

    return (
        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "4px", overflow: "visible" }}>
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-0">
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                    <h5 className="fw-bold m-0 text-primary d-flex align-items-center" style={{ fontSize: "18px" }}>
                        <i className="mdi mdi-chart-areaspline text-primary me-2 font-size-22"></i>
                        Receitas vs Despesas (12 meses)
                    </h5>
                    <div className="d-flex align-items-center gap-4 flex-wrap">
                        <div className="text-end">
                            <small className="text-muted d-block fw-bold font-size-11 text-uppercase">Total Receitas</small>
                            <span className="fw-bold font-size-18" style={{ color: COLORS.income.hover }}>{formatSmallCurrency(totalIncome)}</span>
                        </div>
                        <div className="text-end">
                            <small className="text-muted d-block fw-bold font-size-11 text-uppercase">Total Despesas</small>
                            <span className="fw-bold font-size-18" style={{ color: COLORS.expense.hover }}>{formatSmallCurrency(totalExpense)}</span>
                        </div>
                        <div className="text-end p-2 rounded" style={{ backgroundColor: "#f8fafc" }}>
                            <small className="text-muted d-block fw-bold font-size-11 text-uppercase mb-1">Resultado Líquido</small>
                            <span className={`fw-bold font-size-18`} style={{ color: totalNet >= 0 ? COLORS.net.hover : COLORS.expense.hover }}>
                                {formatSmallCurrency(totalNet)}
                            </span>
                        </div>
                    </div>
                </div>
            </CardHeader>
            <CardBody className="p-4 pt-1 position-relative" style={{ overflow: "visible" }}>
                
                {/* Legenda */}
                <div className="d-flex gap-4 justify-content-center w-100 mb-4">
                    <div className="d-flex align-items-center gap-1">
                        <span className="rounded-circle shadow-sm" style={{ width: "10px", height: "10px", backgroundColor: COLORS.income.hover }} />
                        <span className="text-muted font-size-11 fw-bold">RECEITAS</span>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                        <span className="rounded-circle shadow-sm" style={{ width: "10px", height: "10px", backgroundColor: COLORS.expense.hover }} />
                        <span className="text-muted font-size-11 fw-bold">DESPESAS</span>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                        <span className="rounded-circle shadow-sm" style={{ width: "10px", height: "10px", backgroundColor: COLORS.net.line }} />
                        <span className="text-muted font-size-11 fw-bold">LÍQUIDO</span>
                    </div>
                </div>

                <div style={{ height: "300px", position: "relative", paddingLeft: "55px", paddingRight: "25px", paddingTop: "10px" }}>
                    
                    {/* Eixo Y */}
                    <div style={{ position: "absolute", left: 0, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", fontSize: "11px", color: "#9ca3af", fontWeight: "600", paddingBottom: "25px" }}>
                        <span className="text-end" style={{ width: "45px" }}>{formatSmallCurrency(maxVal)}</span>
                        <span className="text-end" style={{ width: "45px" }}>{formatSmallCurrency(maxVal * 0.75)}</span>
                        <span className="text-end" style={{ width: "45px" }}>{formatSmallCurrency(maxVal * 0.5)}</span>
                        <span className="text-end" style={{ width: "45px" }}>{formatSmallCurrency(maxVal * 0.25)}</span>
                        <span className="text-end" style={{ width: "45px" }}>0</span>
                    </div>

                    {/* Linhas de Grade */}
                    <div style={{ position: "absolute", left: "55px", right: "25px", height: "calc(100% - 25px)", top: "0px", display: "flex", flexDirection: "column", justifyContent: "space-between", pointerEvents: "none", zIndex: 1 }}>
                        <div style={{ borderBottom: "1px dashed #e5e7eb", height: "0" }}></div>
                        <div style={{ borderBottom: "1px dashed #e5e7eb", height: "0" }}></div>
                        <div style={{ borderBottom: "1px dashed #e5e7eb", height: "0" }}></div>
                        <div style={{ borderBottom: "1px dashed #e5e7eb", height: "0" }}></div>
                        <div style={{ borderBottom: "1px solid #d1d5db", height: "0" }}></div>
                    </div>

                    {/* SVG das Linhas e Áreas */}
                    <svg
                        style={{
                            position: "absolute",
                            top: 0,
                            left: "55px",
                            width: "calc(100% - 80px)",
                            height: "calc(100% - 25px)",
                            pointerEvents: "none",
                            zIndex: 4
                        }}
                        preserveAspectRatio="none"
                        viewBox="0 0 100 100"
                    >
                        <defs>
                            <linearGradient id="areaGradientIncome" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={COLORS.income.base} stopOpacity="0.4" />
                                <stop offset="100%" stopColor={COLORS.income.base} stopOpacity="0.0" />
                            </linearGradient>
                            <linearGradient id="areaGradientExpense" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={COLORS.expense.base} stopOpacity="0.4" />
                                <stop offset="100%" stopColor={COLORS.expense.base} stopOpacity="0.0" />
                            </linearGradient>
                            
                            {/* Sombra para linha de líquido */}
                            <filter id="netShadow" x="-20%" y="-20%" width="140%" height="140%">
                                <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor={COLORS.net.line} floodOpacity="0.4" />
                            </filter>
                        </defs>

                        {/* Receita Area/Linha */}
                        <path d={incomeArea} fill="url(#areaGradientIncome)" stroke="none" />
                        <path
                            d={incomePath}
                            fill="none"
                            stroke={COLORS.income.hover}
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            vectorEffect="non-scaling-stroke"
                        />

                        {/* Despesa Area/Linha */}
                        <path d={expenseArea} fill="url(#areaGradientExpense)" stroke="none" />
                        <path
                            d={expensePath}
                            fill="none"
                            stroke={COLORS.expense.hover}
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            vectorEffect="non-scaling-stroke"
                        />

                        {/* Líquido Linha */}
                        <path
                            d={netPath}
                            fill="none"
                            stroke={COLORS.net.line}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            vectorEffect="non-scaling-stroke"
                            filter="url(#netShadow)"
                        />
                    </svg>

                    {/* Camada interativa e Hover (Tooltips) */}
                    <div className="h-100 w-100 d-flex position-relative align-items-end justify-content-between" style={{ paddingBottom: "25px", zIndex: 10 }}>
                        {chartData.map((item, idx) => {
                            const isHovered = hoveredIdx === idx;

                            const incomeY = incomePoints[idx].y;
                            const expenseY = expensePoints[idx].y;
                            const netY = netPoints[idx].y;

                            return (
                                <div
                                    key={idx}
                                    className="h-100 d-flex flex-column align-items-center justify-content-end position-relative"
                                    style={{ width: `${100 / chartData.length}%`, cursor: "pointer" }}
                                    onMouseEnter={() => setHoveredIdx(idx)}
                                    onMouseLeave={() => setHoveredIdx(null)}
                                >
                                    {/* Tooltip Centralizado */}
                                    {isHovered && (
                                        <div 
                                            className="position-absolute bg-white shadow align-items-center rounded-3 p-3"
                                            style={{ 
                                                bottom: "60%", 
                                                width: "200px",
                                                left: "50%", 
                                                transform: "translate(-50%, 50%)",
                                                pointerEvents: "none",
                                                zIndex: 9999,
                                                border: "1px solid #e5e7eb"
                                            }}
                                        >
                                            <h6 className="fw-bold mb-3 pb-2 border-bottom text-center text-primary">{item.display}</h6>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted small d-flex align-items-center">
                                                    <span className="rounded-circle me-1 shadow-sm" style={{ width: "8px", height: "8px", backgroundColor: COLORS.income.hover }} />
                                                    Receitas
                                                </span>
                                                <span className="fw-bold" style={{ color: COLORS.income.hover }}>{formatFullCurrency(item.income)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted small d-flex align-items-center">
                                                    <span className="rounded-circle me-1 shadow-sm" style={{ width: "8px", height: "8px", backgroundColor: COLORS.expense.hover }} />
                                                    Despesas
                                                </span>
                                                <span className="fw-bold" style={{ color: COLORS.expense.hover }}>{formatFullCurrency(item.expense)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between pt-2 mt-2 border-top">
                                                <span className="fw-bold small d-flex align-items-center text-muted">
                                                    Líquido
                                                </span>
                                                <span className="fw-bold" style={{ color: item.net >= 0 ? COLORS.net.hover : COLORS.expense.hover }}>
                                                    {formatFullCurrency(item.net)}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Guia vertical */}
                                    {isHovered && (
                                        <div
                                            className="position-absolute"
                                            style={{
                                                bottom: 0,
                                                height: `100%`,
                                                width: "1px",
                                                backgroundColor: "rgba(26, 37, 66, 0.12)",
                                                pointerEvents: "none",
                                                zIndex: 5
                                            }}
                                        ></div>
                                    )}

                                    {/* Pontos sobre as linhas */}
                                    {isHovered && (
                                        <>
                                            {/* Receitas */}
                                            <div
                                                className="position-absolute"
                                                style={{
                                                    bottom: `${100 - incomeY}%`,
                                                    width: "10px",
                                                    height: "10px",
                                                    borderRadius: "50%",
                                                    backgroundColor: "#fff",
                                                    border: `2.5px solid ${COLORS.income.hover}`,
                                                    transform: "translate(0, 50%)",
                                                    zIndex: 20,
                                                    boxShadow: "0 2px 4px rgba(0,0,0,0.15)"
                                                }}
                                            />
                                            {/* Despesas */}
                                            <div
                                                className="position-absolute"
                                                style={{
                                                    bottom: `${100 - expenseY}%`,
                                                    width: "10px",
                                                    height: "10px",
                                                    borderRadius: "50%",
                                                    backgroundColor: "#fff",
                                                    border: `2.5px solid ${COLORS.expense.hover}`,
                                                    transform: "translate(0, 50%)",
                                                    zIndex: 20,
                                                    boxShadow: "0 2px 4px rgba(0,0,0,0.15)"
                                                }}
                                            />
                                            {/* Líquido */}
                                            <div
                                                className="position-absolute"
                                                style={{
                                                    bottom: `${100 - netY}%`,
                                                    width: "12px",
                                                    height: "12px",
                                                    borderRadius: "50%",
                                                    backgroundColor: "#fff",
                                                    border: `3px solid ${COLORS.net.line}`,
                                                    transform: "translate(0, 50%)",
                                                    zIndex: 20,
                                                    boxShadow: "0 2px 4px rgba(0,0,0,0.15)"
                                                }}
                                            />
                                        </>
                                    )}

                                    <span
                                        className="position-absolute text-muted fw-bold"
                                        style={{
                                            bottom: "-25px",
                                            fontSize: "12px",
                                            color: isHovered ? "#111827" : "#9ca3af",
                                            transition: "color 0.2s"
                                        }}
                                    >
                                        {item.display}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                
                <div className="mt-4 text-center pb-2">
                    <span 
                        className="text-muted text-uppercase fw-bold bg-light px-3 py-1 rounded-pill" 
                        style={{ fontSize: "11px", letterSpacing: "1px" }}
                    >
                        Fluxo de Caixa: Faturamento vs Gastos vs Resultado Líquido
                    </span>
                </div>
            </CardBody>
        </Card>
    );
};

export default RevenueExpenseChart;


